<template>
  <Breadcrumb :links="pageBreadcrumbs"/>
  <div
      class="clinic-profile"
      :class="{'clinic-profile--fab-active': isBookingNarrow}"
      itemscope
      itemtype="https://schema.org/Organization https://schema.org/LocalBusiness https://schema.org/MedicalOrganization https://schema.org/VeterinaryCare"
  >
    <div class="clinic-profile__columns">
      <div class="clinic-profile__main">
        <div class="clinic-profile__head">
          <div class="clinic-profile__logo">
            <UIAvatar
                :fullName="clinic.name"
                :photo="clinic.logo || '/images/no-photo.webp'"
                :id="clinic.id"
                :add-review-button="false"
                model="clinics"
                isDetail
                fit="cover"
                width="160"
                height="160"
                sizes="160px xs:80px sm:80px md:160px"
                preload
            />
          </div>
          <div class="clinic-profile__meta">
            <div class="clinic-profile__title-row">
              <h1 class="clinic-profile__name" itemprop="name">
                {{ clinic.name }}<span
                    v-if="clinic.isProfileComplete"
                    class="clinic-profile__shield"
                >&nbsp;<UIProfileCompleteBadge/></span><ClinicOwnerClaimLink
                    v-if="!clinic.userId"
                    :clinic-id="clinic.id"
                />
              </h1>
            </div>
            <div class="clinic-profile__rating-row" aria-label="Рейтинг и отзывы">
              <template v-if="showRatingStats">
                <div class="clinic-profile__rating-segment">
                  <SvgoRatingStar class="clinic-profile__star" aria-hidden="true"/>
                  <span>{{ clinic.rating }}</span>
                </div>
                <span class="clinic-profile__rating-div" aria-hidden="true"/>
              </template>
              <NuxtLink v-if="reviewCount > 0" class="clinic-profile__rating-link" to="#reviews">
                {{ reviewsCountLabel }}
              </NuxtLink>
              <span v-else class="clinic-profile__rating-muted">{{ reviewsCountLabel }}</span>
              <span class="clinic-profile__rating-div" aria-hidden="true"/>
              <ReviewAddButton
                  :id="clinic.id"
                  model="clinics"
                  :is-show-icon="false"
                  class-list="clinic-profile__review-btn"
              />
            </div>
            <div v-if="headTags.length" class="clinic-profile__tags">
              <span v-for="tag in headTags" :key="tag" class="clinic-profile__tag">{{ tag }}</span>
            </div>
            <p v-if="kinds.length" class="clinic-profile__kinds">
              <template v-for="(kind, index) in kinds" :key="kind">
                <span v-if="index" class="clinic-profile__kind-sep" aria-hidden="true"> | </span>
                <span>{{ kind }}</span>
              </template>
            </p>
          </div>
        </div>

        <ClientOnly>
          <VeterinarProfileTopReviewsSlider :reviews="clinic.featuredReviews ?? []"/>
        </ClientOnly>

        <VeterinarProfileSectionTabs
            v-if="sectionTabs.length"
            :tabs="sectionTabs"
            :active-hash="resolvedNavHash"
            aria-label="Разделы клиники"
        />

        <ClinicDetailBooking v-if="isBookingNarrow" class="clinic-profile__scroll" :clinic="clinic"/>

        <section id="about" class="clinic-profile__section clinic-profile__scroll">
          <h2 class="clinic-profile__section-title">О клинике</h2>
          <ClinicGallery
              v-if="clinic.photos?.length"
              :photos="clinic.photos"
              :clinic-name="clinic.name"
              :slides-to-scroll="1"
          />
          <div v-show="clinic.about.length" class="clinic-detail__about">
            <div
                :class="{ 'clinic-detail__about-text--collapsed': isAboutCollapsed }"
                class="clinic-detail__about-text"
                ref="aboutText"
                itemprop="description"
            >{{ clinic.about }}
            </div>
            <button
                type="button"
                class="clinic-detail__more-btn"
                v-if="isShowViewMore"
                :aria-expanded="!isAboutCollapsed"
                @click="toggleAbout"
            >
              <span class="clinic-detail__more-text">{{ isAboutCollapsed ? 'Показать еще' : 'Свернуть' }}</span>
              <SvgoShowMore
                  class="clinic-detail__more-icon"
                  :class="{'clinic-detail__more-icon--rotate': !isAboutCollapsed}"
                  aria-hidden="true"
              />
            </button>
          </div>
          <p v-if="cityName" class="clinic-profile__city">Город: {{ cityName }}</p>
        </section>

        <section v-if="animals.length" class="clinic-profile__section">
          <h2 class="clinic-profile__section-title">С какими животными работаем</h2>
          <div class="clinic-profile__pills">
            <span v-for="animal in animals" :key="animal.id" class="clinic-profile__pill">{{ animal.name }}</span>
          </div>
        </section>

        <section id="doctors" class="clinic-profile__section clinic-profile__scroll">
          <ClinicVeterinarians :clinicId="clinic.id" @nearest-slot="onNearestSlot"/>
        </section>

        <section
            v-if="clinic.services?.length"
            id="services"
            class="clinic-profile__section clinic-profile__scroll"
        >
          <h2 class="clinic-profile__section-title">Услуги в клинике</h2>
          <ClinicServices :clinic="clinic" :servicesData="clinic.services"/>
          <p v-if="clinic.isFromOpenSource && !clinic.userId" class="clinic-detail__services-note">
            Данные о ценах и услугах взяты из открытых источников. Актуально на {{ clinic.updatedAt }}
          </p>
        </section>

        <section v-if="hasShop" id="shop" class="clinic-profile__section clinic-profile__scroll">
          <h2 class="clinic-profile__section-title">Корма и ветаптека</h2>
          <p class="clinic-profile__shop-text">
            В клинике можно купить корма и ветеринарные препараты после приёма — без отдельной поездки в магазин.
          </p>
          <div class="clinic-profile__pills">
            <span v-if="hasPharmacy" class="clinic-profile__pill">Ветаптека</span>
            <span v-if="hasFeed" class="clinic-profile__pill">Продажа кормов</span>
          </div>
          <ShopClinicProducts v-if="shopStock?.data.length" :stock="shopStock"/>
        </section>

        <section
            v-if="clinic.features?.length"
            id="gear"
            class="clinic-profile__section clinic-profile__scroll"
        >
          <ClinicFeatures :groups="clinic.features"/>
        </section>

        <section
            id="reviews"
            class="clinic-profile__section clinic-profile__scroll"
        >
          <ReviewList
              emptyText="Здесь пока нет отзывов. Давайте оставим?"
              model="clinics"
              :id="clinic.id"
              :rating="clinic.rating"
              :reviewsCount="clinic.reviewCount"
              :modelName="clinic.name"
              :modelAvatar="clinic?.logo !== '' ? clinic?.logo ?? undefined : undefined"
          />
        </section>

        <section id="address" class="clinic-profile__section clinic-profile__scroll">
          <h2 class="clinic-profile__section-title">Адрес клиники</h2>
          <div v-if="clinic.address" class="clinic-profile__copy-row">
            <span>{{ clinic.address }}</span>
            <button type="button" class="clinic-detail__copy-btn" aria-label="Копировать адрес" @click="copyAddress">
              <SvgoCopy class="clinic-detail__copy-icon" aria-hidden="true"/>
            </button>
          </div>
          <div v-if="hasMetro" class="clinic-detail__metro">
            <div
                v-for="metro in clinic.metroStations"
                :key="metro.id"
                class="clinic-detail__metro-station"
            >
              <span
                  v-if="metro.line.color"
                  class="clinic-detail__metro-line"
                  :style="{ backgroundColor: metro.line.color }"
                  :title="metro.line.name"
              />
              <span class="clinic-detail__metro-name">{{ metro.name }}</span>
              <span v-if="metro.distanceKm" class="clinic-detail__metro-distance">
                {{ formatDistance(metro.distanceKm) }}
              </span>
            </div>
          </div>
          <div
              v-if="clinic.location && Object.values(clinic.location).length"
              class="clinic-detail__container clinic-detail__container--map"
          >
      <div :class="{'clinic-detail__map--fullscreen': isMapVisible}"
           class="clinic-map clinic-detail__map" id="clinic-map-container"
           @click.stop="() => {if(isMapVisible == false) isMapVisible = true}"
      >
        <yandex-map
            v-model="map"
            :height="isMapVisible?'800px':'280px'"
            :settings="{
              location: {
                center: coord,
                zoom: 16,
              },
              behaviors: enabledBehaviors,
            }"
            width="100%"
        >
          <yandex-map-default-scheme-layer :settings="{theme: isDark ? 'dark' : 'light'}"/>
          <yandex-map-default-features-layer/>
          <yandex-map-controls v-if="isMapVisible" :settings="{ position: 'right' }">
            <yandex-map-zoom-control/>
          </yandex-map-controls>
          <yandex-map-marker
              :settings="{ coordinates: coord, onClick: () => {if(isMapVisible) {openMarker = !openMarker}}, zIndex: (openMarker ? 1 : 0) }"
              class="clinic-map__marker">
            <SvgoMapPoint class="clinic-map__icon" filled aria-hidden="true"/>
            <div v-if="openMarker" class="clinic-map__popup" @click.stop="openMarker = false">
              <SvgoClose class="clinic-map__popup-close" aria-hidden="true"></SvgoClose>
              <div class="clinic-map__popup-content">
                <div class="clinic-map__popup-title">{{ clinic.name }}</div>
                <div class="clinic-map__popup-row">
                  <SvgoAddress class="clinic-map__popup-icon" aria-hidden="true"/>
                  {{ clinic.address }}
                </div>
                <div class="clinic-map__popup-row">
                  <SvgoTime class="clinic-map__popup-icon" aria-hidden="true"/>
                  {{ clinic.schedule }}
                </div>
              </div>
            </div>

          </yandex-map-marker>
        </yandex-map>
        <div v-if="openMarker && isMapVisible" class="clinic-map__popup clinic-map__popup--bottom"
             @click.stop="openMarker = false">
          <SvgoClose class="clinic-map__popup-close" aria-hidden="true"></SvgoClose>
          <div class="clinic-map__popup-content">
            <div class="clinic-map__popup-title">{{ clinic.name }}</div>
            <div class="clinic-map__popup-row">
              <SvgoAddress class="clinic-map__popup-icon" aria-hidden="true"/>
              {{ clinic.address }}
            </div>
            <div class="clinic-map__popup-row">
              <SvgoTime class="clinic-map__popup-icon" aria-hidden="true"/>
              {{ clinic.schedule }}
            </div>
          </div>
        </div>
        <button v-show="!isMapVisible" type="button" class="clinic-detail__map-fullscreen-icon"
                :aria-label="'Развернуть карту'" @click="() => { if(!isMapVisible) isMapVisible = true }">
          <SvgoFullscreen aria-hidden="true"/>
        </button>
        <button v-show="isMapVisible" type="button" class="clinic-detail__map-close-icon" :aria-label="'Закрыть карту'"
                @click.stop="() => {isMapVisible = false; openMarker = false}">
          <SvgoClose aria-hidden="true"/>
        </button>
      </div>
          </div>
          <p v-if="clinic.seoHubs?.metro || clinic.seoHubs?.rayon" class="clinic-profile__hubs">
            <NuxtLink v-if="clinic.seoHubs?.metro" :to="clinic.seoHubs.metro.path">
              Клиники у метро {{ clinic.seoHubs.metro.label }}
            </NuxtLink>
            <NuxtLink v-if="clinic.seoHubs?.rayon" :to="clinic.seoHubs.rayon.path">
              Клиники в {{ clinic.seoHubs.rayon.label }}
            </NuxtLink>
          </p>
        </section>
      </div>

      <aside v-if="!isBookingNarrow" class="clinic-profile__aside" aria-label="Запись в клинику">
        <ClinicDetailBooking class="clinic-profile__scroll" :clinic="clinic"/>
      </aside>
    </div>

    <section
        v-if="clinic.nearbyClinics?.length"
        class="clinic-profile__related"
    >
      <h2 class="clinic-profile__section-title">Ещё клиники рядом</h2>
      <div class="clinic-detail__related-list">
        <ClinicCard
            v-for="item in clinic.nearbyClinics"
            :key="item.id"
            :clinic="item"
        />
      </div>
    </section>
    <section
        v-if="clinic.networkClinics?.length"
        class="clinic-profile__related"
    >
      <h2 class="clinic-profile__section-title">Другие клиники этой сети</h2>
      <div class="clinic-detail__related-list">
        <ClinicCard
            v-for="item in clinic.networkClinics"
            :key="item.id"
            :clinic="item"
        />
      </div>
    </section>

    <div class="clinic-detail__disclaimer">
      <template v-if="clinic.isFromOpenSource">Текст создан на основе данных из открытых источников. Компания не несет
        ответственность за точность и достоверность сведений.
      </template>
      <template v-else-if="clinic.updatedAt">Информация редактировалась представителем клиники {{
          clinic.updatedAt
        }}
      </template>
    </div>

    <ClientOnly>
      <Teleport to="body">
        <div v-if="isBookingNarrow" class="clinic-detail-fab-anchor">
          <ClinicDetailFab :nearest-slot="nearestSlot" @navigate="onFabNavigate"/>
        </div>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import type {TClinicDetail} from '~/types/clinic'
import type {BehaviorType, LngLat, YMap} from '@yandex/ymaps3-types';
import {
  YandexMap, YandexMapControls,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultSchemeLayer,
  YandexMapMarker,
  YandexMapZoomControl,
} from 'vue-yandex-maps';
import {buildClinicSeoDescription, buildClinicSeoTitle} from '~/utils/clinicSeoMeta';
import {declineWord, formatNumberWithSpaces} from '~/utils/number';
import {getPlural} from '~/utils/helpers';
import {useMediaQuery} from '~/composables/useMediaQuery';
import {useVeterinarProfileTabsScrollSpy} from '~/composables/useVeterinarProfileTabsScrollSpy';
import type {VeterinarProfileSectionTab} from '~/components/Veterinar/ProfileSectionTabs.vue';
import type {VeterinarianBookingNearestSlot} from '~/types/veterinar';

const {isDark} = useColorTheme();

const props = defineProps<{
  clinic: TClinicDetail
}>()

const isBookingNarrow = useMediaQuery('(max-width: 1024px)');
const nearestSlot = ref<VeterinarianBookingNearestSlot | null>(null);
const isAboutCollapsed = ref<boolean>(true);

// Helper to get city name regardless of whether it's a string or object
const cityName = computed(() => typeof props.clinic.city === 'object' ? props.clinic.city.name : props.clinic.city);
const cityPrepositional = computed(() => {
  const city = props.clinic.city
  if (typeof city === 'object' && city?.nameCases?.prepositional_singular) {
    return `в ${city.nameCases.prepositional_singular}`
  }
  const name = cityName.value || ''
  if (!name) return ''
  // Fallback без nameCases: «Москва» → «в Москве».
  if (/а$/i.test(name)) return `в ${name.slice(0, -1)}е`
  return `в ${name}`
})
const aboutText = ref<HTMLElement | null>(null);
const map = shallowRef<YMap | null>(null);

useClinicDetailSchema(computed(() => props.clinic))

const seoTitle = computed(() => buildClinicSeoTitle(props.clinic, cityName.value || ''))
const seoDescription = computed(() => buildClinicSeoDescription(props.clinic, cityPrepositional.value || `в ${cityName.value}`))
const isIndexable = computed(() => props.clinic.isIndexable === true)

const isShowViewMore = computed(() => {
  return aboutText.value && aboutText.value.scrollHeight > aboutText.value.clientHeight;
})

const toggleAbout = (): void => {
  isAboutCollapsed.value = !isAboutCollapsed.value
}

const {copyToClipboard} = useClipboard();

const copyAddress = (): void => {
  copyToClipboard(props.clinic.address, {
    successMessage: 'Адрес скопирован в буфер обмена'
  });
}

const scrollToMap = (): void => {
  const mapElement = document.getElementById('clinic-map-container')
  if (mapElement) {
    mapElement.scrollIntoView({behavior: 'smooth'})
  }
}
const config = useRuntimeConfig();

useHead({
  title: () => seoTitle.value,
  meta: [
    {
      name: 'robots',
      content: () => isIndexable.value ? 'index, follow' : 'noindex, follow',
    },
    {
      name: 'googlebot',
      content: () => isIndexable.value ? 'index, follow' : 'noindex, follow',
    },
    {
      name: 'description',
      content: () => seoDescription.value
    },
    // OpenGraph метатеги
    {
      property: 'og:title',
      content: () => seoTitle.value
    },
    {
      property: 'og:description',
      content: () => seoDescription.value
    },
    {
      property: 'og:image',
      content: () => `${config.public.canonicalDomain}/api/v1/images/clinic-og/${props.clinic.id}` || '/logo.svg'
    },
    {
      property: 'twitter:image',
      content: () => `${config.public.canonicalDomain}/api/v1/images/clinic-og/${props.clinic.id}` || '/logo.svg'
    },
    {
      property: 'twitter:card',
      content: () => 'summary_large_image'
    }
  ]
});

// Хлебные крошки
const pageBreadcrumbs = computed(() => [
  {name: 'Клиники', link: '/clinics/'},
  {name: cityName.value, link: `/clinics/${props.clinic.citySlug}/`},
  {name: props.clinic.name}
]);

const isMapVisible = ref<boolean>(false);
const openMarker = ref(false);

const coord = computed<LngLat>(() => {
  return [props.clinic?.location?.longitude, props.clinic?.location?.latitude] as LngLat;
});
const enabledBehaviors = computed((): BehaviorType[] => {
  return isMapVisible.value ? ['dblClick', 'drag', 'scrollZoom', 'mouseRotate', 'mouseTilt', 'magnifier', 'oneFingerZoom', 'panTilt', 'pinchZoom'] : [];
});

const hasMetro = computed(() => {
  return props.clinic.metroStations && props.clinic.metroStations.length > 0;
});

const formatDistance = (distance: number) => {
  if (distance < 1) {
    return `${Math.round(distance * 1000)} м`;
  } else {
    return `${distance.toFixed(1)} км`;
  }
};

const reviewCount = computed(() => props.clinic.reviewCount ?? 0);
const showRatingStats = computed(() => {
  const r = props.clinic.rating;
  return Boolean(r && parseFloat(String(r)) > 0);
});
const reviewsCountLabel = computed(() => {
  const n = reviewCount.value;
  if (n <= 0) {
    return 'Нет отзывов';
  }
  return `${formatNumberWithSpaces(n)} ${declineWord(n, ['отзыв', 'отзыва', 'отзывов'])}`;
});
const kinds = computed(() => (props.clinic.kinds ?? []).map((k) => k.trim()).filter(Boolean));
const animals = computed(() => props.clinic.animals ?? []);
const shopEnabled = isShopEnabled();
const hasPharmacy = computed(() =>
    (props.clinic.featureCodes ?? []).includes('pharmacy')
    || kinds.value.includes('Ветаптека'),
);
const hasFeed = computed(() => kinds.value.includes('Продажа кормов'));
const {data: shopStock} = useShopClinicProducts(props.clinic.id);
const hasShop = computed(() => shopEnabled && (hasPharmacy.value || hasFeed.value || !!shopStock.value?.data.length));
const doctorsCount = computed(() => props.clinic.doctorsCount ?? 0);
const headTags = computed(() => {
  const tags: string[] = [];
  if (props.clinic.openedYear) {
    tags.push(`С ${props.clinic.openedYear} года`);
  }
  if (doctorsCount.value > 0) {
    tags.push(`${doctorsCount.value} ${getPlural(doctorsCount.value, ['врач', 'врача', 'врачей'])}`);
  }
  return tags;
});

const sectionTabs = computed<VeterinarProfileSectionTab[]>(() => {
  const tabs: VeterinarProfileSectionTab[] = [];
  if (isBookingNarrow.value) {
    tabs.push({hash: '#book', label: 'Записаться'});
  }
  tabs.push({hash: '#about', label: 'О клинике'});
  tabs.push({
    hash: '#doctors',
    label: 'Врачи',
    count: doctorsCount.value,
  });
  if (props.clinic.services?.length) {
    tabs.push({hash: '#services', label: 'Услуги'});
  }
  if (hasShop.value) {
    tabs.push({hash: '#shop', label: 'Корма и ветаптека'});
  }
  if (props.clinic.features?.length) {
    tabs.push({hash: '#gear', label: 'Оснащение'});
  }
  tabs.push({
    hash: '#reviews',
    label: 'Отзывы',
    reviewsCountBadge: reviewCount.value,
  });
  if (props.clinic.address || props.clinic.location) {
    tabs.push({hash: '#address', label: 'Адрес'});
  }
  return tabs;
});

function normalizeHash(hash: string | undefined | null): string {
  const t = String(hash ?? '').trim();
  if (!t) {
    return '';
  }
  return t.startsWith('#') ? t : `#${t}`;
}

const spyTabs = computed(() => sectionTabs.value.filter((tab) => tab.hash !== '#book'));
const {activeHash: scrollSpyHash} = useVeterinarProfileTabsScrollSpy(spyTabs, 128);
const route = useRoute();
const resolvedNavHash = computed(() => {
  const fromRoute = normalizeHash(route.hash);
  const fromSpy = normalizeHash(scrollSpyHash.value);
  if (fromRoute === '#book') {
    return '#book';
  }
  if (import.meta.server) {
    return fromRoute || '#about';
  }
  return fromSpy || fromRoute || '#about';
});

function onNearestSlot(slot: VeterinarianBookingNearestSlot | null) {
  nearestSlot.value = slot;
}

function onFabNavigate() {
  const target = nearestSlot.value ? 'doctors' : 'book';
  document.getElementById(target)?.scrollIntoView({behavior: 'smooth', block: 'start'});
}
</script>

<style lang="scss">
@use "~/assets/css/components/clinic-map.scss";

.clinic-profile {
  background: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;

  &--fab-active {
    @include media(md) {
      padding-bottom: calc(96px + env(safe-area-inset-bottom));
    }
  }

  &__columns {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 24px;
    align-items: start;

    @include media(md) {
      grid-template-columns: 1fr;
    }
  }

  &__main {
    min-width: 0;
    display: flex;
    flex-direction: column;
    background: $color-white;
    box-shadow: 0 4px 10px 0 rgba(138, 138, 176, 0.07);

    @include media(md, min-width) {
      border-radius: 24px;
      padding: 32px 36px 40px;
    }

    @include media(md) {
      border-radius: 20px;
      padding: 16px 14px 24px;
    }
  }

  &__aside {
    position: sticky;
    top: 114px;
    align-self: start;
    min-width: 0;
  }

  &__head {
    display: grid;
    grid-template-columns: 160px minmax(0, 1fr);
    gap: 24px;
    align-items: start;
    margin-bottom: 20px;

    @include media(md) {
      grid-template-columns: 80px minmax(0, 1fr);
      gap: 12px;
    }
  }

  &__logo {
    flex-shrink: 0;

    .avatar {
      margin: 0;
      overflow: hidden;
      width: 160px;
      height: 160px;
      border-radius: 50%;
    }

    .avatar__media,
    .avatar__image,
    .avatar__initials,
    .avatar__skeleton {
      width: 160px;
      height: 160px;
      border-radius: 50%;
      overflow: hidden;
    }

    @include media(md) {
      .avatar,
      .avatar__media,
      .avatar__image,
      .avatar__initials,
      .avatar__skeleton {
        width: 80px;
        height: 80px;
      }
    }
  }

  &__meta {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__title-row {
    min-width: 0;
  }

  &__name {
    margin: 0;
    font-size: 32px;
    font-weight: 500;
    line-height: 1.1;
    min-width: 0;

    @include media(md) {
      font-size: 20px;
    }

    // Узкая колонка рядом с лого: меньше кегль, чтобы щит оставался в конце названия.
    @include media(sm) {
      font-size: 16px;
    }

    .clinic-owner-claim {
      margin-left: 10px;
      vertical-align: middle;
    }
  }

  // inline-flex + middle: щит по центру кегля последней строки, не по baseline и не по блоку h1.
  &__shield {
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    white-space: nowrap;

    // Как у vet-profile: 24px рядом с крупным h1 (не 18px — на фоне 32px щит терялся).
    .profile-complete-badge {
      width: 24px;
      height: 24px;
    }

    .profile-complete-badge__icon,
    svg {
      width: 24px;
      height: 24px;
    }
  }

  &__rating-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 12px;
    font-size: 16px;
    line-height: 1.35;
    white-space: nowrap;

    @include media(md) {
      gap: 8px;
      font-size: 14px;
    }
  }

  &__rating-segment {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__star {
    width: 16px;
    height: 16px;
    color: $color-rating-yellow;
  }

  &__rating-div {
    width: 1px;
    min-height: 16px;
    align-self: stretch;
    background: $color-light-gray;
    flex-shrink: 0;
  }

  &__rating-link {
    color: inherit;
    text-decoration: none;
    font-weight: 400;

    &:hover {
      color: $color-accent-text;
    }
  }

  &__rating-muted {
    color: $color-gray;
  }

  &__review-btn {
    appearance: none;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    font-weight: 400;
    color: inherit;
    cursor: pointer;

    &:hover {
      color: $color-accent-text;
    }
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    border: 1px solid $color-schedule-grid-line;
    border-radius: 8px;
    background: $color-white;
    font-size: 14px;
    font-weight: 500;
  }

  &__kinds {
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 8px;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.35;

    @include media(md) {
      font-size: 14px;
    }
  }

  &__kind-sep {
    color: $color-text-subtle;
    font-weight: 400;
  }

  .vet-profile-top-reviews {
    margin: 8px 0 16px;
  }

  .vet-profile-section-tabs {
    margin: 8px 0 24px;
    overflow: hidden;
  }

  .vet-profile-section-tabs__inner {
    gap: 20px;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }

    &::after {
      content: '';
      flex: 0 0 16px;
      align-self: stretch;
    }
  }

  .vet-profile-section-tabs__link {
    color: $color-gray;
    padding: 10px 0 12px;
    border-bottom-width: 2px;
  }

  .vet-profile-section-tabs__link--active,
  .vet-profile-section-tabs__link:hover {
    color: $color-accent-text;
  }

  .vet-profile-section-tabs__count {
    color: inherit;
  }

  &__section {
    margin-bottom: 28px;
  }

  &__section-title {
    margin: 0 0 12px;
    font-size: 20px;
    font-weight: 500;
    line-height: 1.2;
  }

  &__city {
    margin: 12px 0 0;
    color: $color-gray;
    font-size: 15px;
  }

  &__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__pill {
    padding: 8px 14px;
    border-radius: 20px;
    background: $color-dark-white;
    font-size: 14px;
  }

  &__shop-text {
    margin: 0 0 12px;
    color: $color-black;
    font-size: 15px;
    line-height: 1.5;
  }

  &__copy-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    color: $color-gray;
    font-size: 15px;
  }

  &__hubs {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 16px;
    margin: 12px 0 0;

    a {
      color: $color-accent-text;
    }
  }

  &__scroll {
    scroll-margin-top: 120px;
  }

  &__related {
    margin-top: 16px;
  }
}

.clinic-detail {
  display: flex;
  flex-direction: column;
  gap: 40px;

  &__container {
    display: flex;
    background-color: $color-white;
    border-radius: 60px;
    padding-top: 48px;
    padding-bottom: 48px;
    padding-left: 48px;
    padding-right: 260px;
    gap: 52px 52px;

    @include media(sm) {
      padding: 12px;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      border-radius: 32px;
      gap: 12px;
    }

    &--map {
      padding: 0;
      position: relative;
      flex-direction: column;
      gap: 24px;

      @include media(sm) {
        align-items: stretch;
      }
    }

    &--reviews {
      padding: 48px;

      .reviews-section {
        width: 640px;
        max-width: 100%;
        margin-inline: auto;
      }

      @include media(sm) {
        padding: 12px;
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    min-width: 0;
    max-width: 100%;
    @include media(sm) {
      align-items: normal;
      width: 100%;
    }
  }

  &__header {
    @include media(sm) {
      .avatar {
        width: 140px;
        margin-inline: auto;
        margin-bottom: 0 !important;
      }

      .avatar > a {
        display: block;
        width: 100%;
      }

      .avatar__image {
        display: block;
        width: 100% !important;
        height: auto !important;
        aspect-ratio: 1/1;
        border-radius: 80px !important;
        object-fit: cover;
      }

      .avatar .review-button,
      .avatar .review-button .button-text {
        white-space: nowrap;
        padding: 0;
        margin-left: 0;
        margin-right: 0;
      }
    }
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 4px;
    line-height: 28px;
    flex-wrap: wrap;

    @include media(sm) {
      justify-content: center;
    }
  }

  &__title-group {
    display: inline;
    max-width: 100%;
  }

  &__title {
    margin: 0;
    display: inline;

    @include media(sm) {
      font-size: 24px;
    }
  }

  &__badge {
    display: inline-flex;
    margin-left: 4px;
    vertical-align: middle;
    white-space: nowrap;
  }

  &__owner-wrap {
    font-size: 48px;
    line-height: 54px;
    @include media(sm) {
      line-height: 24px;
    }
  }

  &__owner-button {
    padding: 5px 12px;
    background-color: $color-dark-white;
    border-radius: 50px;
    font-size: 12px;
    line-height: 1.2;
    color: $color-gray;
    white-space: nowrap;
    cursor: pointer;
    display: inline-block;
    vertical-align: center;
    transform: translateY(-10px);
    margin-left: 10px;

    @include media(sm) {
      transform: translateY(-4px);
    }

    &:hover {
      background: #EE66E01A;
    }
  }

  &__subtitle {
    color: $color-black;
    text-align: center;
    font-size: 32px;
    font-style: normal;
    font-weight: 500;
    line-height: 120%;
    margin: 0;
  }

  &__hub-link {
    color: inherit;
    text-decoration: none;

    &:hover {
      color: $color-accent-text;
      text-decoration: underline;
    }
  }

  &__seo-hubs {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 40px;
  }

  &__related {
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
    padding: 48px;
    padding-right: 48px;

    @include media(sm) {
      padding: 16px;
      gap: 16px;
      align-items: stretch;
    }
  }

  &__related-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
  }

  &__review-button {
    .review-button {
      margin-top: 0;
    }
  }

  &__about-text {
    color: $color-black;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.4;
    white-space: pre-line;
    overflow-wrap: anywhere;
    transition: .3s;

    &--collapsed {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -moz-box;
      display: -webkit-box;
      line-clamp: 4;
      -webkit-line-clamp: 4;
      box-orient: vertical;
      -webkit-box-orient: vertical;
    }
  }

  &__more-btn {
    appearance: none;
    border: none;
    font-size: 15px;
    fill: $color-accent-text;
    color: $color-accent-text;
    background-color: $color-white;
    border-radius: 0 0 0 0;
    padding: 0 0 0 0;
    display: flex;
    gap: 4px;
    cursor: pointer;
    line-height: 1.4;
    align-items: flex-end;
  }

  &__more-icon {
    &--rotate {
      transform: scale(-1);
    }
  }

  &__tags {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__address {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  &__schedule {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 16px
  }

  &__schedule-text {
    white-space: pre-line;
  }

  &__schedule .icon--accent {
    font-size: 18px;
    color: $color-accent-text;
    flex-shrink: 0;
  }

  &__address {
    font-size: 16px
  }

  &__address .icon--accent {
    font-size: 18px;
    color: $color-accent-text;
    flex-shrink: 0;
  }

  &__map-btn {
    appearance: none;
    border: none;
    font-size: 15px;
    color: $color-accent-text;
    background-color: $color-white;
    padding: 0;
    cursor: pointer;
    line-height: 1.4;
    margin-left: 26px;
  }

  &__map {
    overflow: hidden;
    border-radius: 16px;
    height: 220px;
    position: relative;
    transition: .3s;
    cursor: pointer;

    @include media(sm) {
      border-radius: 32px;
    }
    /*width: calc(100vw - 122px);
    @include media(md) {
      width: calc(100vw - 40px) !important;
    }

    @include media(sm) {
      width: calc(100vw - (0.54em * 2)) !important;
    }*/


    &--fullscreen {
      height: 800px !important;
      width: calc(100% - 122px) !important;
      max-height: calc(100dvh - 92px);
      position: fixed;
      left: 61px;
      top: 50%;
      transform: translateY(-50%);
      z-index: 1000;
      overflow: hidden;
      cursor: default;

      &::before {
        content: "";
        background-color: rgba(0, 0, 0, .2);
        position: fixed;
        left: -50vw !important;
        top: -50vh !important;
        width: calc(100vw * 2);
        height: calc(100vh * 2);
        z-index: 2;
        cursor: pointer;
      }

      & .__ymap {
        z-index: 10 !important;
        height: 100% !important;
      }

      @include media(sm) {
        width: calc(100vw - 24px) !important;
        left: 12px;
        max-height: calc(100dvh - 24px);
        top: 12px;
        transform: none;
      }
    }
  }


  &__metro-list {
    padding-left: 26px;
  }

  &__metro {
    display: flex;
    flex-direction: row;
    gap: 8px 16px;
    width: 100%;
    flex-wrap: wrap;
    margin-bottom: 12px;
  }

  &__metro-station {
    display: flex;
    align-items: baseline;
    flex-wrap: nowrap;
    gap: 4px;
  }

  &__metro-line {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-right: 8px;
    margin-left: 2px;
  }

  &__metro-name {
    min-width: 0;
  }

  &__metro-distance {
    color: $color-gray;
    font-size: 14px;
    flex-shrink: 0;
  }

  &__map-fullscreen-icon {
    appearance: none;
    border: none;
    font: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    width: 40px;
    height: 40px;
    padding: 10px;
    background: $color-white;
    border-radius: 50%;
    transform: translateX(-50%);
    cursor: pointer;
    color: $color-accent-text;
    fill: $color-accent-text;
    z-index: 31;
    left: unset;
    right: -6px;
    top: 12px;
    @include media(sm) {
      transform: none;
      right: 16px;
    }

    &:hover {
      box-shadow: 0 0 10px 2px rgba(0, 0, 0, .2);
    }
  }

  &__map:hover &__map-fullscreen-icon {
    box-shadow: 0 0 10px 2px rgba(0, 0, 0, .2);
  }

  &__map-close-icon {
    appearance: none;
    border: none;
    font: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    width: 40px;
    height: 40px;
    padding: 10px;
    background: $color-white;
    border-radius: 50%;
    top: 48px;
    right: 48px;
    transform: translateX(-50%);
    cursor: pointer;
    fill: $color-accent-text;
    color: $color-accent-text;
    z-index: 31;
    @include media(sm) {
      right: 16px;
      top: 16px;
    }

    &:hover {
      box-shadow: 0 0 10px 2px rgba(0, 0, 0, .2);
    }
  }

  &__all-address {
    display: flex;
    gap: 8px;
    margin: 0 40px;
    flex-direction: column;
    width: 640px;

    @include media(sm) {
      width: auto;
      margin: 0 10px;
      padding: 0 12px;
    }

    &-title {
      padding-top: 32px;
      color: $color-black;
      font-size: 24px;
      font-weight: 500;
      line-height: 28px;
      margin: 0;
    }

    &-info {
      color: $color-gray;
      font-size: 16px;
      font-weight: 400;
      line-height: 22px;
      display: flex;
      gap: 8px;
    }
  }

  &__copy-btn {
    appearance: none;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
  }

  &__copy-icon {
    fill: $color-gray;
    font-size: 20px;
    cursor: pointer;

    &:hover {
      fill: $color-accent-text;
    }
  }

  &__actions {
    display: flex;
    gap: 12px;
    flex-wrap: nowrap;

    .button,
    .clinic-call-button {
      flex: 0 1 auto;
      min-width: 0;
    }

    .button:only-child {
      flex: none;
      width: 100%;
    }
  }

  &__disclaimer {
    font-size: 14px;
    line-height: 135%;
    text-align: center;
    color: $color-text-subtle;

    @include media(sm) {
      font-size: 12px;
    }
  }

  &__services-note {
    margin: 12px 0 0;
    font-size: 14px;
    line-height: 135%;
    color: $color-text-subtle;

    @include media(sm) {
      font-size: 12px;
    }
  }
}

.clinic-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 14px;

  &__item {
    padding: 8px;
    border-radius: 6px;
    background-color: $color-tag-success;
    color: $color-tag-success-text;

    &:nth-child(5n+2) {
      background-color: $color-tag-warning;
      color: $color-tag-warning-text;
    }

    &:nth-child(5n+3) {
      background-color: $color-tag-info;
      color: $color-tag-info-text;
    }

    &:nth-child(5n+4) {
      background-color: $color-pink;
      color: $color-accent-text;
    }

    &:nth-child(5n+5) {
      background-color: $color-info;
      color: $color-info-text;
    }
  }
}
</style>
